import type { EntryKey } from "@pinia/colada";
import type { AgentDeployChatWidget } from "@/schemas/agent-deploy-chat-widget.schema";
import type {
  AgentDeployTelegram,
  AgentDeployTelegramBody,
  TelegramConfiguration,
  TelegramUserAccess,
  TelegramUserAccessBody,
} from "@/schemas/agent-deploy-telegram.schema";
import type {
  AgentDeployWhatsappBaileys,
  AgentDeployWhatsappBaileysBody,
} from "@/schemas/agent-deploy-whatsapp-baileys.schema";
import type {
  AgentDeployWhatsapp,
  AgentDeployWhatsappBody,
} from "@/schemas/agent-deploy-whatsapp.schema";
import type { AgentDeploySip } from "@/schemas/agent.schema";
import { defineQuery, useMutation, useQuery, useQueryCache } from "@pinia/colada";
import { computed } from "vue";
import { api } from "@/services/api";
import { ORGANIZATION_DID_QUERY_KEYS } from "./organization-did.query";

// Inline key to avoid circular import with agents.query.ts
const AGENTS_ROOT_KEY = ["agents"] as const;

/**
 * Query keys factory for deployments following Pinia Colada best practices
 *
 * @see https://pinia-colada.esm.dev/guide/query-keys.html
 */
export const DEPLOYMENT_QUERY_KEYS = {
  root: ["deployments"] as const,
  sip: () => [...DEPLOYMENT_QUERY_KEYS.root, "sip"] as const,
  whatsapp: () => [...DEPLOYMENT_QUERY_KEYS.root, "whatsapp"] as const,
  whatsappBaileys: () => [...DEPLOYMENT_QUERY_KEYS.root, "whatsapp-baileys"] as const,
  telegram: () => [...DEPLOYMENT_QUERY_KEYS.root, "telegram"] as const,
  chatWidget: () => [...DEPLOYMENT_QUERY_KEYS.root, "chat-widget"] as const,
  telegramUsers: (agentId: string) => [...DEPLOYMENT_QUERY_KEYS.root, "telegram", "users", agentId] as const,
  telegramConfiguration: (agentId: string) => [...DEPLOYMENT_QUERY_KEYS.root, "telegram", "configuration", agentId] as const,
};

// ==================== SIP DEPLOYMENTS ====================

/**
 * Query hook for fetching all SIP deployments for the organization
 *
 * Features:
 * - Organization-scoped (backend filters by organization_id)
 * - Smart caching with 30s stale time
 * - Automatic deduplication of concurrent requests
 * - Shared cache across components
 *
 * @example
 * ```ts
 * const sipDeploysQuery = useSipDeploysQuery();
 *
 * // Find deployment for specific agent
 * const agentDeploy = computed(() =>
 *   sipDeploysQuery.data.value?.find(d => d.agent_id === agentId)
 * );
 * ```
 */
export const useSipDeploysQuery = defineQuery(() => {
  return useQuery({
    key: DEPLOYMENT_QUERY_KEYS.sip() as EntryKey,
    query: async () => {
      const { data } = await api.get<AgentDeploySip[]>("/agent-deploy-sip");
      return data;
    },
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60_000, // 5 minutes
  });
});

/**
 * Create or update SIP deployment for an agent
 *
 * Automatically invalidates SIP deployments list after mutation
 *
 * @example
 * ```ts
 * const mutation = useCreateOrUpdateSipDeploy();
 *
 * await mutation.mutateAsync({
 *   agentId: "agent-123",
 *   body: {
 *     enable_inbound: true,
 *     enable_outbound: false,
 *   }
 * });
 * ```
 */
export function useCreateOrUpdateSipDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { agentId: string; body: any }) => {
      const { data } = await api.put<AgentDeploySip>(
        `/agent-deploy-sip/${payload.agentId}`,
        payload.body
      );
      return data;
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.sip() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
      queryCache.invalidateQueries({ key: ORGANIZATION_DID_QUERY_KEYS.root as EntryKey });
    },
  });
}

// ==================== WHATSAPP DEPLOYMENTS ====================

/**
 * Query hook for fetching all WhatsApp deployments for the organization
 *
 * Features:
 * - Organization-scoped (backend filters by organization_id)
 * - Smart caching with 30s stale time
 * - Automatic deduplication of concurrent requests
 *
 * @example
 * ```ts
 * const whatsappDeploysQuery = useWhatsappDeploysQuery();
 *
 * // Find deployment for specific agent
 * const agentDeploy = computed(() =>
 *   whatsappDeploysQuery.data.value?.find(d => d.agent_id === agentId)
 * );
 * ```
 */
export const useWhatsappDeploysQuery = defineQuery(() => {
  return useQuery({
    key: DEPLOYMENT_QUERY_KEYS.whatsapp() as EntryKey,
    query: async () => {
      const { data } = await api.get<AgentDeployWhatsapp[]>("/agent-deploy-whatsapp");
      return data;
    },
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60_000, // 5 minutes
  });
});

/**
 * Create WhatsApp deployment
 *
 * Automatically invalidates WhatsApp deployments list after creation
 */
export function useCreateWhatsappDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (body: AgentDeployWhatsappBody) => {
      const { data } = await api.post<AgentDeployWhatsapp>("/agent-deploy-whatsapp", body);
      return data;
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsapp() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Update WhatsApp deployment configuration
 *
 * Automatically invalidates WhatsApp deployments list after update
 */
export function useUpdateWhatsappDeployConfig() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { id: string; configuration: any }) => {
      await api.patch(`/agent-deploy-whatsapp/${payload.id}/configuration`, payload.configuration);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsapp() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Activate WhatsApp deployment with optimistic update
 *
 * Updates UI immediately and rollbacks if request fails
 */
export function useActivateWhatsappDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (agentId: string) => {
      await api.post(`/agent-deploy-whatsapp/${agentId}/activate`);
    },
    onMutate: async (agentId: string) => {
      // Cancel outgoing queries
      queryCache.cancelQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsapp() as any });

      // Snapshot previous value
      const previous = queryCache.getQueryData(DEPLOYMENT_QUERY_KEYS.whatsapp());

      // Optimistically update
      queryCache.setQueryData(DEPLOYMENT_QUERY_KEYS.whatsapp(), (old?: AgentDeployWhatsapp[]) => {
        if (!old) return old;
        return old.map((deploy) =>
          deploy.agent_id === agentId ? { ...deploy, disabled: false } : deploy
        );
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryCache.setQueryData(DEPLOYMENT_QUERY_KEYS.whatsapp(), context.previous);
      }
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsapp() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Deactivate WhatsApp deployment with optimistic update
 *
 * Updates UI immediately and rollbacks if request fails
 */
export function useDeactivateWhatsappDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (agentId: string) => {
      await api.post(`/agent-deploy-whatsapp/${agentId}/deactivate`);
    },
    onMutate: async (agentId: string) => {
      // Cancel outgoing queries
      queryCache.cancelQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsapp() as any });

      // Snapshot previous value
      const previous = queryCache.getQueryData(DEPLOYMENT_QUERY_KEYS.whatsapp());

      // Optimistically update
      queryCache.setQueryData(DEPLOYMENT_QUERY_KEYS.whatsapp(), (old?: AgentDeployWhatsapp[]) => {
        if (!old) return old;
        return old.map((deploy) =>
          deploy.agent_id === agentId ? { ...deploy, disabled: true } : deploy
        );
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryCache.setQueryData(DEPLOYMENT_QUERY_KEYS.whatsapp(), context.previous);
      }
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsapp() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Delete WhatsApp deployment
 */
export function useDeleteWhatsappDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (id: string) => {
      await api.delete(`/agent-deploy-whatsapp/${id}`);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsapp() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Register WhatsApp phone number
 *
 * This mutation registers a phone number with WhatsApp Business API
 * Requires phone_number_id and PIN from Meta/WhatsApp
 */
export function useRegisterWhatsappNumber() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { agentId: string; phone_number_id: string; pin: string }) => {
      const { data } = await api.post<{ success: boolean }>(
        `/agent-deploy-whatsapp/${payload.agentId}/register-number`,
        {
          phone_number_id: payload.phone_number_id,
          pin: payload.pin,
        }
      );
      return data;
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsapp() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

// ==================== WHATSAPP BAILEYS DEPLOYMENTS ====================

/**
 * Query hook for fetching all WhatsApp Baileys deployments for the organization
 *
 * Features:
 * - Organization-scoped (backend filters by organization_id)
 * - Smart caching with 30s stale time
 * - Automatic deduplication of concurrent requests
 *
 * @example
 * ```ts
 * const whatsappBaileysDeploysQuery = useWhatsappBaileysDeploysQuery();
 *
 * // Find deployment for specific agent
 * const agentDeploy = computed(() =>
 *   whatsappBaileysDeploysQuery.data.value?.find(d => d.agent_id === agentId)
 * );
 * ```
 */
export const useWhatsappBaileysDeploysQuery = defineQuery(() => {
  return useQuery({
    key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as EntryKey,
    query: async () => {
      const { data } = await api.get<AgentDeployWhatsappBaileys[]>(
        "/agent-deploy-whatsapp-baileys"
      );
      return data;
    },
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60_000, // 5 minutes
  });
});

/**
 * Create WhatsApp Baileys deployment
 *
 * Automatically invalidates WhatsApp Baileys deployments list after creation
 */
export function useCreateWhatsappBaileysDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (body: AgentDeployWhatsappBaileysBody) => {
      const { data } = await api.post<AgentDeployWhatsappBaileys>(
        "/agent-deploy-whatsapp-baileys",
        body
      );
      return data;
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Initialize WhatsApp Baileys session and get QR code
 *
 * This starts the Baileys session and returns a QR code for WhatsApp authentication
 */
export function useInitWhatsappBaileysSession() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (agentId: string) => {
      const { data } = await api.post<{ qrcode: string }>(
        `/agent-deploy-whatsapp-baileys/${agentId}/init-session`
      );
      return data;
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Stop WhatsApp Baileys session
 *
 * Disconnects the WhatsApp session and clears authentication
 */
export function useStopWhatsappBaileysSession() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (agentId: string) => {
      await api.post(`/agent-deploy-whatsapp-baileys/${agentId}/stop-session`);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Update WhatsApp Baileys deployment configuration
 *
 * Automatically invalidates WhatsApp Baileys deployments list after update
 */
export function useUpdateWhatsappBaileysDeployConfig() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { id: string; configuration: any }) => {
      await api.patch(
        `/agent-deploy-whatsapp-baileys/${payload.id}/configuration`,
        payload.configuration
      );
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Activate WhatsApp Baileys deployment with optimistic update
 *
 * Updates UI immediately and rollbacks if request fails
 */
export function useActivateWhatsappBaileysDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (agentId: string) => {
      await api.post(`/agent-deploy-whatsapp-baileys/${agentId}/activate`);
    },
    onMutate: async (agentId: string) => {
      // Cancel outgoing queries
      queryCache.cancelQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any });

      // Snapshot previous value
      const previous = queryCache.getQueryData(DEPLOYMENT_QUERY_KEYS.whatsappBaileys());

      // Optimistically update
      queryCache.setQueryData(
        DEPLOYMENT_QUERY_KEYS.whatsappBaileys(),
        (old?: AgentDeployWhatsappBaileys[]) => {
          if (!old) return old;
          return old.map((deploy) =>
            deploy.agent_id === agentId ? { ...deploy, disabled: false } : deploy
          );
        }
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryCache.setQueryData(DEPLOYMENT_QUERY_KEYS.whatsappBaileys(), context.previous);
      }
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Deactivate WhatsApp Baileys deployment with optimistic update
 *
 * Updates UI immediately and rollbacks if request fails
 */
export function useDeactivateWhatsappBaileysDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (agentId: string) => {
      await api.post(`/agent-deploy-whatsapp-baileys/${agentId}/deactivate`);
    },
    onMutate: async (agentId: string) => {
      // Cancel outgoing queries
      queryCache.cancelQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any });

      // Snapshot previous value
      const previous = queryCache.getQueryData(DEPLOYMENT_QUERY_KEYS.whatsappBaileys());

      // Optimistically update
      queryCache.setQueryData(
        DEPLOYMENT_QUERY_KEYS.whatsappBaileys(),
        (old?: AgentDeployWhatsappBaileys[]) => {
          if (!old) return old;
          return old.map((deploy) =>
            deploy.agent_id === agentId ? { ...deploy, disabled: true } : deploy
          );
        }
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryCache.setQueryData(DEPLOYMENT_QUERY_KEYS.whatsappBaileys(), context.previous);
      }
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Delete WhatsApp Baileys deployment
 */
export function useDeleteWhatsappBaileysDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (id: string) => {
      await api.delete(`/agent-deploy-whatsapp-baileys/${id}`);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

// ==================== TELEGRAM DEPLOYMENTS ====================

export const useTelegramDeploysQuery = defineQuery(() => {
  return useQuery({
    key: DEPLOYMENT_QUERY_KEYS.telegram() as EntryKey,
    query: async () => {
      const { data } = await api.get<AgentDeployTelegram[]>("/agent-deploy-telegram");
      return data;
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
});

export function useCreateTelegramDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (body: AgentDeployTelegramBody) => {
      const { data } = await api.post<AgentDeployTelegram>("/agent-deploy-telegram", body);
      return data;
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.telegram() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

export function useActivateTelegramDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (agentId: string) => {
      await api.post(`/agent-deploy-telegram/${agentId}/activate`);
    },
    onMutate: async (agentId: string) => {
      queryCache.cancelQueries({ key: DEPLOYMENT_QUERY_KEYS.telegram() as any });
      const previous = queryCache.getQueryData(DEPLOYMENT_QUERY_KEYS.telegram());
      queryCache.setQueryData(
        DEPLOYMENT_QUERY_KEYS.telegram(),
        (old?: AgentDeployTelegram[]) => {
          if (!old) return old;
          return old.map((deploy) =>
            deploy.agentId === agentId ? { ...deploy, disabled: false } : deploy
          );
        }
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryCache.setQueryData(DEPLOYMENT_QUERY_KEYS.telegram(), context.previous);
      }
    },
    onSettled: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.telegram() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

export function useDeactivateTelegramDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (agentId: string) => {
      await api.post(`/agent-deploy-telegram/${agentId}/deactivate`);
    },
    onMutate: async (agentId: string) => {
      queryCache.cancelQueries({ key: DEPLOYMENT_QUERY_KEYS.telegram() as any });
      const previous = queryCache.getQueryData(DEPLOYMENT_QUERY_KEYS.telegram());
      queryCache.setQueryData(
        DEPLOYMENT_QUERY_KEYS.telegram(),
        (old?: AgentDeployTelegram[]) => {
          if (!old) return old;
          return old.map((deploy) =>
            deploy.agentId === agentId ? { ...deploy, disabled: true } : deploy
          );
        }
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryCache.setQueryData(DEPLOYMENT_QUERY_KEYS.telegram(), context.previous);
      }
    },
    onSettled: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.telegram() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

export function useDeleteTelegramDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (id: string) => {
      await api.delete(`/agent-deploy-telegram/${id}`);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.telegram() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

export function useTelegramConfigurationQuery(agentId: () => string) {
  return useQuery({
    key: () => DEPLOYMENT_QUERY_KEYS.telegramConfiguration(agentId()) as EntryKey,
    query: async () => {
      const { data } = await api.get<TelegramConfiguration>(
        `/agent-deploy-telegram/${agentId()}/configuration`,
      );
      return data;
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

export function useUpdateTelegramConfiguration() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { agentId: string; config: Partial<TelegramConfiguration> }) => {
      await api.patch(
        `/agent-deploy-telegram/${payload.agentId}/configuration`,
        payload.config,
      );
    },
    onSettled: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.telegram() as any });
    },
  });
}

export function useTelegramUsersQuery(agentId: () => string) {
  return useQuery({
    key: () => DEPLOYMENT_QUERY_KEYS.telegramUsers(agentId()) as EntryKey,
    query: async () => {
      const { data } = await api.get<TelegramUserAccess[]>(
        `/agent-deploy-telegram/${agentId()}/users`,
      );
      return data;
    },
    staleTime: 15_000,
    gcTime: 5 * 60_000,
  });
}

export function useUpdateTelegramUserStatus() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { agentId: string; body: TelegramUserAccessBody }) => {
      await api.patch(
        `/agent-deploy-telegram/${payload.agentId}/users`,
        payload.body,
      );
    },
    onSuccess: (_data, payload) => {
      queryCache.invalidateQueries({
        key: DEPLOYMENT_QUERY_KEYS.telegramUsers(payload.agentId) as any,
      });
    },
  });
}

// ==================== CHAT WIDGET DEPLOYMENTS ====================

/**
 * Query hook for fetching all Chat Widget deployments for the organization
 *
 * Features:
 * - Organization-scoped (backend filters by organization_id)
 * - Smart caching with 30s stale time
 * - Automatic deduplication of concurrent requests
 *
 * @example
 * ```ts
 * const chatWidgetDeploysQuery = useChatWidgetDeploysQuery();
 *
 * // Find deployment for specific agent
 * const agentDeploy = computed(() =>
 *   chatWidgetDeploysQuery.data.value?.find(d => d.agent_id === agentId)
 * );
 * ```
 */
export const useChatWidgetDeploysQuery = defineQuery(() => {
  return useQuery({
    key: DEPLOYMENT_QUERY_KEYS.chatWidget() as EntryKey,
    query: async () => {
      const { data } = await api.get<AgentDeployChatWidget[]>("/agent-deploy-chat-widget");
      return data;
    },
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60_000, // 5 minutes
  });
});

/**
 * Create Chat Widget deployment
 *
 * Automatically invalidates Chat Widget deployments list after creation
 */
export function useCreateChatWidgetDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { agentId: string; body: any }) => {
      const { data } = await api.post<AgentDeployChatWidget>(
        `/agent-deploy-chat-widget/${payload.agentId}`,
        payload.body
      );
      return data;
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.chatWidget() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

/**
 * Update Chat Widget deployment with optimistic update
 *
 * Automatically invalidates Chat Widget deployments list after update
 * Supports optimistic updates for instant UI feedback
 */
export function useUpdateChatWidgetDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { agentId: string; body: any }) => {
      await api.patch(`/agent-deploy-chat-widget/${payload.agentId}`, payload.body);
    },
    onMutate: async (payload: { agentId: string; body: any }) => {
      // Cancel outgoing queries
      queryCache.cancelQueries({ key: DEPLOYMENT_QUERY_KEYS.chatWidget() as any });

      // Snapshot previous value
      const previous = queryCache.getQueryData(DEPLOYMENT_QUERY_KEYS.chatWidget());

      // Optimistically update
      queryCache.setQueryData(
        DEPLOYMENT_QUERY_KEYS.chatWidget(),
        (old?: AgentDeployChatWidget[]) => {
          if (!old) return old;
          return old.map((deploy) =>
            deploy.agent_id === payload.agentId ? { ...deploy, ...payload.body } : deploy
          );
        }
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryCache.setQueryData(DEPLOYMENT_QUERY_KEYS.chatWidget(), context.previous);
      }
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.chatWidget() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

export function useDeleteChatWidgetDeploy() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (agentId: string) => {
      await api.delete(`/agent-deploy-chat-widget/${agentId}`);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.chatWidget() as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
  });
}

// ==================== COMBINED QUERIES ====================

/**
 * Wrapper hook that fetches all deployment types in parallel
 *
 * Use this when you need all deployment types (e.g., in agents table)
 *
 * @example
 * ```ts
 * const deploymentsQueries = useDeploymentsQueries();
 *
 * // Check if any query is loading
 * if (deploymentsQueries.isLoading.value) {
 *   return <Spinner />;
 * }
 *
 * // Access individual deployment types
 * const sipDeploys = deploymentsQueries.sip.data.value;
 * const whatsappDeploys = deploymentsQueries.whatsapp.data.value;
 * const whatsappBaileysDeploys = deploymentsQueries.whatsappBaileys.data.value;
 * const widgetDeploys = deploymentsQueries.chatWidget.data.value;
 * ```
 */
export function useDeploymentsQueries() {
  const sip = useSipDeploysQuery();
  const whatsapp = useWhatsappDeploysQuery();
  const whatsappBaileys = useWhatsappBaileysDeploysQuery();
  const telegram = useTelegramDeploysQuery();
  const chatWidget = useChatWidgetDeploysQuery();

  const isLoading = computed(
    () =>
      sip.isLoading.value ||
      whatsapp.isLoading.value ||
      whatsappBaileys.isLoading.value ||
      telegram.isLoading.value ||
      chatWidget.isLoading.value
  );

  const isError = computed(
    () =>
      !!sip.error.value ||
      !!whatsapp.error.value ||
      !!whatsappBaileys.error.value ||
      !!telegram.error.value ||
      !!chatWidget.error.value
  );

  const isSuccess = computed(
    () =>
      !!sip.data.value &&
      !!whatsapp.data.value &&
      !!whatsappBaileys.data.value &&
      !!telegram.data.value &&
      !!chatWidget.data.value
  );

  return {
    sip,
    whatsapp,
    whatsappBaileys,
    telegram,
    chatWidget,
    isLoading,
    isError,
    isSuccess,
  };
}

/**
 * Helper hook to invalidate deployment queries
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateDeployments();
 *
 * // Invalidate all deployments
 * invalidate.all();
 *
 * // Invalidate specific type
 * invalidate.sip();
 * ```
 */
export function useInvalidateDeployments() {
  const queryCache = useQueryCache();

  return {
    all: () => {
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: AGENTS_ROOT_KEY as any });
    },
    sip: () => queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.sip() as any }),
    whatsapp: () => queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsapp() as any }),
    whatsappBaileys: () =>
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.whatsappBaileys() as any }),
    telegram: () =>
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.telegram() as any }),
    chatWidget: () =>
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.chatWidget() as any }),
  };
}
